import { NavLink, useLocation } from 'react-router-dom';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import ChecklistRtlOutlinedIcon from '@mui/icons-material/ChecklistRtlOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import GroupsOutlinedIcon2 from '@mui/icons-material/BadgeOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { Permission } from '@ikomet/shared';
import { useAuthStore } from '@/stores/authStore';
import { useUiStore } from '@/stores/uiStore';
import { WorkspaceSwitcher } from './WorkspaceSwitcher';

export const SIDEBAR_WIDTH = 248;
export const SIDEBAR_WIDTH_COLLAPSED = 68;

interface NavItem {
  label: string;
  to: string;
  icon: typeof SpaceDashboardOutlinedIcon;
  permission?: Permission;
}

const PRIMARY_NAV: NavItem[] = [
  { label: 'Home', to: '/', icon: SpaceDashboardOutlinedIcon },
  { label: 'My Work', to: '/my-work', icon: ChecklistRtlOutlinedIcon },
  { label: 'Projects', to: '/projects', icon: FolderOpenOutlinedIcon },
  { label: 'Timesheet', to: '/timesheet', icon: ScheduleOutlinedIcon },
];

const INSIGHT_NAV: NavItem[] = [
  {
    label: 'Command Center',
    to: '/command-center',
    icon: InsightsOutlinedIcon,
    permission: Permission.REPORT_VIEW,
  },
  {
    label: 'Reports',
    to: '/reports',
    icon: AssessmentOutlinedIcon,
    permission: Permission.REPORT_VIEW,
  },
  {
    label: 'Utilisation',
    to: '/resources',
    icon: GroupsOutlinedIcon,
    permission: Permission.REPORT_VIEW,
  },
  {
    label: 'Approvals',
    to: '/approvals',
    icon: FactCheckOutlinedIcon,
    permission: Permission.TIME_APPROVE,
  },
];

const ADMIN_NAV: NavItem[] = [
  { label: 'Workspaces', to: '/workspaces', icon: FolderOpenOutlinedIcon, permission: Permission.ORG_VIEW },
  { label: 'People', to: '/people', icon: GroupsOutlinedIcon2, permission: Permission.ORG_VIEW },
  { label: 'Clients', to: '/clients', icon: BusinessOutlinedIcon, permission: Permission.ORG_VIEW },
  { label: 'Settings', to: '/settings', icon: SettingsOutlinedIcon },
];

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUiStore();
  const can = useAuthStore((s) => s.can);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  const width = sidebarCollapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  const renderGroup = (label: string, items: NavItem[]) => {
    const visible = items.filter((item) => !item.permission || can(item.permission));
    if (!visible.length) return null;

    return (
      <Box key={label} sx={{ px: 1.25 }}>
        {!sidebarCollapsed && (
          <Typography
            variant="overline"
            sx={{ px: 1.25, pt: 2, pb: 0.75, display: 'block', color: 'text.disabled' }}
          >
            {label}
          </Typography>
        )}
        {sidebarCollapsed && <Divider sx={{ my: 1.25 }} />}
        <List disablePadding sx={{ display: 'grid', gap: 0.25 }}>
          {visible.map((item) => {
            const active =
              item.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.to);
            const Icon = item.icon;

            const button = (
              <ListItemButton
                key={item.to}
                component={NavLink}
                to={item.to}
                selected={active}
                sx={{
                  minHeight: 40,
                  px: sidebarCollapsed ? 1.25 : 1.5,
                  justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: sidebarCollapsed ? 0 : 1.5,
                    color: active ? 'primary.main' : 'text.secondary',
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </ListItemIcon>
                {!sidebarCollapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.8125rem',
                      fontWeight: active ? 650 : 550,
                      color: active ? 'primary.main' : 'text.primary',
                    }}
                  />
                )}
              </ListItemButton>
            );

            return sidebarCollapsed ? (
              <Tooltip key={item.to} title={item.label} placement="right">
                <span>{button}</span>
              </Tooltip>
            ) : (
              button
            );
          })}
        </List>
      </Box>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'surface.raised',
        borderRight: 1,
        borderColor: 'divider',
        transition: 'width 180ms cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent={sidebarCollapsed ? 'center' : 'space-between'}
        sx={{ px: sidebarCollapsed ? 0 : 2, py: 1.75, minHeight: 60 }}
      >
        {!sidebarCollapsed && (
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: '#fff',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              iK
            </Box>
            <Box sx={{ lineHeight: 1.1 }}>
              <Typography sx={{ fontWeight: 750, fontSize: '0.875rem' }}>iKomet</Typography>
              <Typography variant="caption" color="text.secondary">
                Delivery Platform
              </Typography>
            </Box>
          </Stack>
        )}
        <IconButton size="small" onClick={toggleSidebar} aria-label="Toggle sidebar">
          {sidebarCollapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Divider />

      <Box sx={{ flex: 1, overflowY: 'auto', pt: 1.25, pb: 2 }}>
        <WorkspaceSwitcher collapsed={sidebarCollapsed} />
        {renderGroup('Workspace', PRIMARY_NAV)}
        {renderGroup('Insights', INSIGHT_NAV)}
        {renderGroup('Admin', ADMIN_NAV)}
      </Box>

      {!sidebarCollapsed && user && (
        <Box
          sx={{
            m: 1.25,
            p: 1.5,
            borderRadius: 2,
            bgcolor: (t) => alpha(t.palette.primary.main, 0.07),
            border: 1,
            borderColor: (t) => alpha(t.palette.primary.main, 0.16),
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
            {user.role.replace('_', ' ').toUpperCase()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Press ⌘K to search anything
          </Typography>
        </Box>
      )}
    </Box>
  );
}
